import { memo, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, TrashIcon, ClockIcon, FireIcon, ShoppingBagIcon, WrenchScrewdriverIcon, HeartIcon } from '@heroicons/react/24/outline'
import { listingAPI } from '../../services/api/listingAPI'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import LoadingSpinner from '../LoadingSpinner'
import { toast } from '../../services/toastService'

const logger = createLogger('MyListingsSection')

const CATEGORY_ICONS = {
  food:       FireIcon,
  goods:      ShoppingBagIcon,
  service:    WrenchScrewdriverIcon,
  mutual_aid: HeartIcon,
}

const CATEGORY_COLORS = {
  food:       'text-accent bg-accent/10',
  goods:      'text-indigo-600 bg-indigo-50',
  service:    'text-primary bg-primary/10',
  mutual_aid: 'text-amber-600 bg-amber-50',
}

function getExpiryStatus(expiresAt, isFulfilled) {
  if (isFulfilled) return { label: 'Fulfilled', cls: 'text-primary bg-primary/10' }
  const diff = new Date(expiresAt) - new Date()
  if (diff <= 0) return { label: 'Expired', cls: 'text-red-500 bg-red-50' }
  const mins = Math.round(diff / 60000)
  if (mins < 60) return { label: `${mins}m left`, cls: 'text-accent bg-accent/10' }
  const hrs = Math.round(mins / 60)
  return { label: `${hrs}h left`, cls: 'text-gray-500 bg-gray-100' }
}

/**
 * MyListingsSection - management area for the user's own ephemeral listings.
 * Shows all listings from the last 7 days with fulfill + delete actions.
 * @idempotent @atomic
 */
const MyListingsSection = memo(function MyListingsSection() {
  const { t } = useTranslation()
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(true)
  const { execute } = useAsyncOperation()

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    listingAPI.getMyListings()
      .then((res) => { if (!cancelled) setListings(res?.data?.listings || []) })
      .catch((err) => {
        if (!cancelled) {
          if (err?.response?.status === 404) {
            logger.warn('Listings endpoint not available yet')
          } else {
            logger.error('Failed to load my listings', err)
          }
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => load(), [load])

  const handleFulfill = useCallback(async (id) => {
    await execute(() => listingAPI.fulfillListing(id), {
      onSuccess: () => {
        toast.success(t('listings.markedFulfilled', 'Marked as fulfilled'))
        setListings((prev) => prev.map((l) => l.id === id ? { ...l, is_fulfilled: true } : l))
      },
      onError: () => toast.error(t('listings.fulfillFailed', 'Failed to update')),
    })
  }, [execute, t])

  const handleDelete = useCallback(async (id) => {
    await execute(() => listingAPI.deleteListing(id), {
      onSuccess: () => {
        toast.success(t('listings.deleted', 'Listing removed'))
        setListings((prev) => prev.filter((l) => l.id !== id))
      },
      onError: () => toast.error(t('listings.deleteFailed', 'Failed to delete')),
    })
  }, [execute, t])

  if (loading) return <LoadingSpinner size="sm" />

  if (!listings.length) return (
    <p className="text-xs text-gray-500 py-3 text-center">{t('listings.empty', 'No listings yet')}</p>
  )

  return (
    <div className="space-y-2 p-2">
      {listings.map((listing) => {
        const Icon = CATEGORY_ICONS[listing.category] || FireIcon
        const catColor = CATEGORY_COLORS[listing.category] || 'text-gray-500 bg-gray-100'
        const expiry = getExpiryStatus(listing.expires_at, listing.is_fulfilled)
        const isActive = !listing.is_fulfilled && new Date(listing.expires_at) > new Date()

        return (
          <div key={listing.id} className="bg-white rounded-md border border-gray-200/60 p-3 space-y-1.5">
            {/* Header row */}
            <div className="flex items-start gap-2">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium flex-shrink-0 ${catColor}`}>
                <Icon className="w-3 h-3" />
                {t(`listings.category.${listing.category}`, listing.category)}
              </span>
              <span className={`ml-auto inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${expiry.cls}`}>
                {expiry.label}
              </span>
            </div>

            {/* Title + price */}
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900 leading-snug break-words flex-1">{listing.title}</p>
              {listing.price != null ? (
                <span className="text-sm font-bold text-primary flex-shrink-0">${Number(listing.price).toFixed(2)}</span>
              ) : (
                <span className="text-xs font-medium text-emerald-600 flex-shrink-0">{t('listings.free', 'Free')}</span>
              )}
            </div>

            {/* Quantity + address */}
            {(listing.quantity || listing.address) && (
              <p className="text-xs text-gray-400 truncate">
                {listing.quantity}{listing.quantity && listing.address ? ' · ' : ''}{listing.address}
              </p>
            )}

            {/* Expiry time */}
            <div className="flex items-center gap-1 text-[10px] text-gray-400">
              <ClockIcon className="w-3 h-3 flex-shrink-0" />
              <span>{new Date(listing.expires_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>

            {/* Actions — only for active listings */}
            {isActive && (
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => handleFulfill(listing.id)}
                  onTouchEnd={(e) => { e.preventDefault(); handleFulfill(listing.id) }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors"
                  style={{ background: 'rgb(var(--color-primary))' }}
                >
                  <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  {t('listings.markFulfilled', 'Mark fulfilled')}
                </button>
                <button
                  onClick={() => handleDelete(listing.id)}
                  onTouchEnd={(e) => { e.preventDefault(); handleDelete(listing.id) }}
                  className="flex items-center justify-center w-8 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
})

export default MyListingsSection
